import React, { useMemo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Pagination } from 'swiper/modules';
import Indicate from '../common/cards/Indicate';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/pagination';
import { fetchIcon } from '../../utils/iconUtils';
import { AnalyticsData } from '../../pages/Analytics';
import useI18n from '../../hooks/useI18n';
import { capitalizeFirstLetter } from '@/utils/caseUtils';

interface TopSourcesSwiperProps {
  topSources?: AnalyticsData['topSources'];
}

const TopSourcesSwiper: React.FC<TopSourcesSwiperProps> = ({
  topSources = [],
}) => {
  const { t } = useI18n();

  const topSourcesData = useMemo(() => {
    return topSources
      .map((source) => {
        return {
          ...source,
          direction:
            source.percentage - source.previousPercentage >= 0 ? 'up' : 'down',
          percent: Number(source.percentage.toFixed(2)),
          logo: fetchIcon(source._id),
          label: source._id,
        };
      })
      .filter(Boolean);
  }, [topSources]);

  return (
    <Swiper
      slidesPerView={5}
      spaceBetween={20}
      freeMode
      breakpoints={{
        200: { slidesPerView: 1 },
        320: { slidesPerView: 1.2 },
        500: { slidesPerView: 2.1 },
        690: { slidesPerView: 2.7 },
        1080: { slidesPerView: 3.4 },
        1300: { slidesPerView: 3.8 },
        1400: { slidesPerView: 5 },
      }}
      modules={[FreeMode, Pagination]}
      className="mySwiper"
    >
      {topSourcesData.map((source, index) => (
        <SwiperSlide
          key={index}
          className="border px-[16px] py-[24px] rounded-3xl bg-white"
        >
          <div className="flex items-center gap-2">
            <img
              src={source.logo ?? ''}
              alt={`${source.label} logo`}
              className="h-[24px] w-[24px]"
            />
            <label>
              {source.label.toLowerCase() === 'unknown'
                ? String(t('common.others'))
                : capitalizeFirstLetter(source.label)}
            </label>
            <Indicate
              direction={(source.direction as 'up' | 'down') ?? 'up'}
              percent={source.percent ?? 0}
            />
          </div>
        </SwiperSlide>
      ))}
    </Swiper>
  );
};

export default TopSourcesSwiper;
